import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import {
  FileText, AlertTriangle, CheckCircle, ArrowLeft, Plus, Calendar, Users
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const ContractsPage = () => {
  const [contracts, setContracts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    employee_id: "",
    contract_type: "permanent",
    start_date: "",
    end_date: "",
    salary: "",
    notice_period_days: "30"
  });
  const navigate = useNavigate();
  const token = localStorage.getItem("cdace_token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [contractsRes, empRes] = await Promise.all([
          axios.get(`${API}/admin/contracts`, { headers }),
          axios.get(`${API}/admin/employees`, { headers })
        ]);
        setContracts(contractsRes.data.contracts || []);
        setEmployees(empRes.data.employees || []);
      } catch (error) {
        console.error("Error:", error);
        if (error.response?.status === 401) navigate("/admin-login");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/admin/contracts`, {
        ...formData,
        salary: parseFloat(formData.salary),
        notice_period_days: parseInt(formData.notice_period_days),
        benefits: ["Medical", "Pension", "Leave"]
      }, { headers });
      toast.success("Contract created");
      setShowForm(false);
      const res = await axios.get(`${API}/admin/contracts`, { headers });
      setContracts(res.data.contracts || []);
    } catch (error) {
      toast.error("Failed to create contract");
    }
  };

  const formatCurrency = (val) => {
    if (!val) return "$0";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(val);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active": return "bg-green-500/20 text-green-400";
      case "expiring_soon": return "bg-yellow-500/20 text-yellow-400";
      case "expired": return "bg-red-500/20 text-red-400";
      default: return "bg-slate-500/20 text-slate-400";
    }
  };

  const getTypeBadge = (type) => {
    const colors = {
      permanent: "bg-blue-500/20 text-blue-400",
      "fixed-term": "bg-purple-500/20 text-purple-400",
      contractor: "bg-orange-500/20 text-orange-400"
    };
    return colors[type] || "bg-slate-500/20 text-slate-400";
  };

  const activeContracts = contracts.filter(c => c.expiry_status === "active").length;
  const expiringContracts = contracts.filter(c => c.expiry_status === "expiring_soon").length;
  const expiredContracts = contracts.filter(c => c.expiry_status === "expired").length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1c2d] flex items-center justify-center">
        <div className="text-[#c9a227] text-xl">Loading Contracts...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b1c2d] p-6" data-testid="contracts-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
              Employee Contracts
            </h1>
            <p className="text-slate-400 text-sm">Manage employment contracts and renewals</p>
          </div>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#c9a227] text-[#0b1c2d] hover:bg-[#c9a227]/90">
          <Plus className="w-4 h-4 mr-2" /> New Contract
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="glass-card">
          <CardContent className="p-4">
            <FileText className="w-6 h-6 text-[#c9a227] mb-2" />
            <p className="text-2xl font-bold text-[#c9a227]">{contracts.length}</p>
            <p className="text-xs text-slate-400">Total Contracts</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <CheckCircle className="w-6 h-6 text-green-400 mb-2" />
            <p className="text-2xl font-bold text-green-400">{activeContracts}</p>
            <p className="text-xs text-slate-400">Active</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <AlertTriangle className="w-6 h-6 text-yellow-400 mb-2" />
            <p className="text-2xl font-bold text-yellow-400">{expiringContracts}</p>
            <p className="text-xs text-slate-400">Expiring Soon</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <Calendar className="w-6 h-6 text-red-400 mb-2" />
            <p className="text-2xl font-bold text-red-400">{expiredContracts}</p>
            <p className="text-xs text-slate-400">Expired</p>
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {(expiringContracts > 0 || expiredContracts > 0) && (
        <div className="mb-6 space-y-2">
          {expiredContracts > 0 && (
            <div className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-sm">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <span className="text-red-400">{expiredContracts} contracts have expired - urgent renewal required</span>
            </div>
          )}
          {expiringContracts > 0 && (
            <div className="flex items-center gap-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              <span className="text-yellow-400">{expiringContracts} contracts expiring within 60 days</span>
            </div>
          )}
        </div>
      )}

      {/* Create Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <Card className="glass-card w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-white">Create Contract</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-slate-400">Employee</label>
                  <select
                    className="w-full mt-1 bg-slate-900 border border-[#c9a227]/20 rounded-sm p-2 text-white"
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    required
                  >
                    <option value="">Select Employee</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.full_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400">Contract Type</label>
                  <select
                    className="w-full mt-1 bg-slate-900 border border-[#c9a227]/20 rounded-sm p-2 text-white"
                    value={formData.contract_type}
                    onChange={(e) => setFormData({ ...formData, contract_type: e.target.value })}
                  >
                    <option value="permanent">Permanent</option>
                    <option value="fixed-term">Fixed-Term</option>
                    <option value="contractor">Contractor</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400">Start Date</label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="input-sovereign"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">End Date (optional)</label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="input-sovereign"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-400">Annual Salary ($)</label>
                    <Input
                      type="number"
                      value={formData.salary}
                      onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                      className="input-sovereign"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Notice Period (days)</label>
                    <Input
                      type="number"
                      value={formData.notice_period_days}
                      onChange={(e) => setFormData({ ...formData, notice_period_days: e.target.value })}
                      className="input-sovereign"
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="bg-[#c9a227] text-[#0b1c2d]">Create</Button>
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Contracts Table */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-slate-200">All Contracts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Type</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Salary</th>
                  <th>Days Remaining</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((contract) => (
                  <tr key={contract.id}>
                    <td className="text-white font-medium">{contract.employee_name}</td>
                    <td>
                      <Badge className={getTypeBadge(contract.contract_type)}>{contract.contract_type}</Badge>
                    </td>
                    <td className="text-slate-300">{contract.start_date}</td>
                    <td className="text-slate-300">{contract.end_date || "Ongoing"}</td>
                    <td className="text-[#c9a227]">{formatCurrency(contract.salary)}</td>
                    <td className={contract.days_remaining < 0 ? "text-red-400" : contract.days_remaining < 60 ? "text-yellow-400" : "text-green-400"}>
                      {contract.days_remaining !== undefined ? `${contract.days_remaining} days` : "N/A"}
                    </td>
                    <td>
                      <Badge className={getStatusBadge(contract.expiry_status)}>
                        {contract.expiry_status?.replace("_", " ") || "active"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContractsPage;
